import React from "react";
import { 
  Box, 
  Card, 
  CardContent, 
  CardHeader, 
  Typography,
  Grid,
  Divider,
  Avatar,
  Chip
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import AccountCircleIcon from "@material-ui/icons/AccountCircle";
import EmailIcon from "@material-ui/icons/Email";
import VpnKeyIcon from "@material-ui/icons/VpnKey";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";

import { PasswordForgetForm } from "../PasswordForget";
import PasswordChangeForm from "../PasswordChange";
import { AuthUserContext, withAuthorization } from "../Session";
import SignOutButton from "../SignOut";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  title: {
    fontWeight: 600,
    color: '#1e1e2d',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  subtitle: {
    color: '#92929f',
    marginTop: theme.spacing(0.5),
  },
  card: {
    borderRadius: '12px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
    marginBottom: theme.spacing(3),
  },
  cardHeader: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: '#ffffff',
    '& .MuiCardHeader-title': {
      fontWeight: 600,
      fontSize: '1.1rem',
    },
  },
  cardContent: {
    padding: theme.spacing(3),
  },
  profileSection: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(3),
    backgroundColor: '#f5f8fa',
    borderRadius: '12px',
    marginBottom: theme.spacing(3),
  },
  avatar: {
    width: theme.spacing(8),
    height: theme.spacing(8),
    backgroundColor: '#667eea',
    fontSize: '2rem',
    fontWeight: 600,
  },
  userInfo: {
    flex: 1,
  },
  emailChip: {
    backgroundColor: '#ffffff',
    border: '1px solid #e0e0e0',
    fontWeight: 500,
    marginTop: theme.spacing(1),
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    color: '#1e1e2d',
    fontWeight: 600,
  },
  sectionIcon: {
    color: '#667eea',
  },
}));

const AccountPage = () => {
  const classes = useStyles();
  
  return (
    <AuthUserContext.Consumer>
      {(context) => {
        const authUser = context?.authUser !== undefined ? context.authUser : context;
        const userEmail = authUser?.email || "";
        const userInitial = userEmail ? userEmail[0].toUpperCase() : "U";
        
        return (
          <Box className={classes.root}>
            {/* Header */}
            <Box className={classes.header}>
              <Typography variant="h4" className={classes.title}>
                <AccountCircleIcon style={{ fontSize: '2rem', color: '#667eea' }} />
                Minha Conta
              </Typography>
              <Typography variant="body2" className={classes.subtitle}>
                Gerencie suas informações pessoais e configurações de segurança
              </Typography>
            </Box>

            <Grid container spacing={3}>
              {/* Profile Card */}
              <Grid item xs={12} md={8}>
                <Card className={classes.card}>
                  <CardHeader 
                    className={classes.cardHeader}
                    title="Informações do Perfil"
                  />
                  <CardContent className={classes.cardContent}>
                    <Box className={classes.profileSection}>
                      <Avatar className={classes.avatar}>
                        {userInitial}
                      </Avatar>
                      <Box className={classes.userInfo}>
                        <Typography variant="h6" style={{ fontWeight: 600, color: '#1e1e2d' }}>
                          Usuário
                        </Typography>
                        <Chip 
                          icon={<EmailIcon style={{ fontSize: '1rem' }} />}
                          label={userEmail}
                          className={classes.emailChip}
                          size="small"
                        />
                      </Box>
                    </Box>

                    <Divider style={{ margin: '24px 0' }} />

                    {/* Password Reset Section */}
                    <Box mb={3}>
                      <Typography variant="h6" className={classes.sectionTitle}>
                        <VpnKeyIcon className={classes.sectionIcon} />
                        Recuperar Senha
                      </Typography>
                      <PasswordForgetForm userEmail={userEmail} />
                    </Box>

                    <Divider style={{ margin: '24px 0' }} />

                    {/* Password Change Section */}
                    <Box mb={3}>
                      <Typography variant="h6" className={classes.sectionTitle}>
                        <VpnKeyIcon className={classes.sectionIcon} />
                        Alterar Senha
                      </Typography>
                      <PasswordChangeForm />
                    </Box>

                    <Divider style={{ margin: '24px 0' }} />

                    {/* Sign Out Section */}
                    <Box>
                      <Typography variant="h6" className={classes.sectionTitle}>
                        <ExitToAppIcon className={classes.sectionIcon} />
                        Encerrar Sessão
                      </Typography>
                      <SignOutButton />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        );
      }}
    </AuthUserContext.Consumer>
  );
};

const condition = (authUser) => !!authUser;

export default withAuthorization(condition)(AccountPage);
